import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { Box, IconButton } from '@material-ui/core'
import clsx from 'clsx'
import ProjectWorkspaceAskDelaChat from 'components/AskDelaChat/ProjectWorkspaceAskDelaChat'
import ChatLauncherIcon from 'components/RightPane/ChatLauncherIcon'
import TrackChangeIcon from 'components/RightPane/TrackChangeIcon'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangesPanel } from 'components/WorkspaceEditor/components/TrackChangesPanel/TrackChangesPanel'
import { useEditorWithSidebar } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, { memo, useCallback, useMemo } from 'react'
import { ProjectProperties } from './ProjectProperties'
import { ToggleHandlerType } from './SidebarToggles'
import { useShowRightMostPane } from './context/RightMostPaneContext'
import { RightMostPaneTabs } from './enums/RightMostPaneTabs'
import { useRightMostPane } from './hooks/useRightMostPane'
import useRightMostPaneStyles from './styles/useRightMostPaneStyles'

export interface RightMostPaneBetaProps {
    drawingDesignation: string
    showRightMostPane: boolean
    toggleHandler: (
        toggleType: ToggleHandlerType,
        value: boolean | undefined
    ) => void
    contentTeamFromStudioPage?: boolean
    setLoadingBackdrop?: (value: boolean) => void
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    setRightMostPaneTab?: (value: string) => void
    selectedProjectElementNode?: ProjectElementNode | null | undefined
}

export const RightMostPaneBeta = memo(
    ({
        drawingDesignation,
        toggleHandler,
        contentTeamFromStudioPage,
        setLoadingBackdrop,
        setRightMostPaneTab,
        selectedProjectElementNode,
    }: RightMostPaneBetaProps) => {
        const showSpecpointCopilot =
            process.env.REACT_APP_ShowSpecpointCopilot === '1'

        const { currentPane, changeTab } = useRightMostPane({
            selectedProjectElementNode,
        })

        const {
            showRightMostPane,
            setShowRightMostPane,
        } = useShowRightMostPane()
        const classes = useRightMostPaneStyles({
            showRightMostPane,
            currentPane,
        })
        const { toggleSidebar, isSidebarVisible } = useEditorWithSidebar()
        const { setIsSuggestionsDataUpdated } = useSuggestionsShallowSelector(
            (state) => ({
                setIsSuggestionsDataUpdated: state.setIsSuggestionsDataUpdated,
            })
        )

        const handleTabChange = useCallback(
            (tab: RightMostPaneTabs) => () => {
                const isClosing = currentPane === tab && showRightMostPane

                setShowRightMostPane?.(!isClosing)

                if (tab === RightMostPaneTabs.TrackChanges) {
                    toggleSidebar(!isClosing)
                    setIsSuggestionsDataUpdated?.(!isClosing)
                }

                if (!isClosing) {
                    changeTab(tab)
                    setRightMostPaneTab?.(tab)
                }
            },
            [
                changeTab,
                setShowRightMostPane,
                setRightMostPaneTab,
                setIsSuggestionsDataUpdated,
                toggleSidebar,
                currentPane,
                showRightMostPane,
            ]
        )

        const renderPaneContent = useMemo(() => {
            switch (currentPane) {
                case RightMostPaneTabs.ProjectProperties:
                    return (
                        <ProjectProperties
                            drawingDesignation={drawingDesignation}
                            elementToggleHandler={toggleHandler}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            setLoadingBackdrop={setLoadingBackdrop}
                        />
                    )
                case RightMostPaneTabs.Keynotes:
                    return (
                        <p className={classes.keynoteText}>
                            Key notes will appear here
                        </p>
                    )
                case RightMostPaneTabs.AskDela:
                    return (
                        showSpecpointCopilot && <ProjectWorkspaceAskDelaChat />
                    )
                default:
                    return null
            }
        }, [
            currentPane,
            drawingDesignation,
            toggleHandler,
            contentTeamFromStudioPage,
            setLoadingBackdrop,
            showSpecpointCopilot,
            classes.keynoteText,
        ])

        const renderIconButton = useCallback(
            (
                tab: RightMostPaneTabs,
                label: string,
                IconComponent: React.ElementType,
                extraClassName?: string,
                disableActiveStyle?: boolean
            ) => (
                <HoverTooltip
                    text={label}
                    placements={['left']}
                    className={clsx(extraClassName)}
                >
                    <IconButton
                        size="small"
                        onClick={handleTabChange(tab)}
                        className={clsx(
                            classes.rightPaneButton,
                            !disableActiveStyle &&
                                currentPane === tab &&
                                classes.active
                        )}
                        aria-label={label}
                    >
                        <IconComponent
                            className={
                                currentPane === tab
                                    ? classes.rightPaneIconActive
                                    : classes.rightPaneIcon
                            }
                        />
                    </IconButton>
                </HoverTooltip>
            ),
            [handleTabChange, classes, currentPane]
        )

        const rightMostPane = useMemo(
            () => (
                <div className={classes.buttonContainer}>
                    {showSpecpointCopilot &&
                        renderIconButton(
                            RightMostPaneTabs.AskDela,
                            'Ask Dela',
                            ChatLauncherIcon,
                            classes.askDelaButton,
                            true
                        )}
                    {renderIconButton(
                        RightMostPaneTabs.TrackChanges,
                        'Track Changes',
                        TrackChangeIcon
                    )}
                </div>
            ),
            [showSpecpointCopilot, renderIconButton, classes]
        )

        if (currentPane === RightMostPaneTabs.TrackChanges) {
            return (
                <Box
                    id="trackChangesPanelId"
                    className={clsx(
                        classes.trackChangesPanel,
                        isSidebarVisible || showRightMostPane
                            ? classes.trackChangesPanelExpanded
                            : classes.trackChangesPanelCollapsed
                    )}
                    display="flex"
                    height="100%"
                >
                    <TrackChangesPanel
                        isSidebarVisible={isSidebarVisible || showRightMostPane}
                    />
                    {rightMostPane}
                </Box>
            )
        }

        return (
            <div
                className={classes.fullWidthRightPaneKeynotesContainer}
                id="rightPanelId"
            >
                {showRightMostPane && (
                    <div
                        className={classes.rightPaneInnerContainer}
                        id="rightMostPaneId"
                    >
                        {renderPaneContent}
                    </div>
                )}
                {rightMostPane}
            </div>
        )
    }
)
